import { useCallback, useRef } from 'react';
import { createOrResumeSession, terminateSession } from '../../services/sessionService';
import { getChatSessionDetails } from '../../services/chatSessionApi';
import { getAccessToken } from '../../utils/tokenStorage';
const parseTimestamp = (timestamp) => {
    if (!timestamp) return new Date(0);
    try {
        const date = new Date(timestamp);
        return isNaN(date.getTime()) ? new Date(0) : date;
    } catch (e) {
        return new Date(0);
    }
};
export const useSessionInitializer = ({
    isAuthenticated,
    markSessionAsInitialized,
    resetSessionInitialization,
    setIsSubscriptionRequired,
    addMessage,
    removeMessage,
    setIsInitializing,
    setProcessingStatus,
    setTimelineItems,
    setHasAuthFailed,
    setSessionContext,
    setTeamName,
    setSessionId,
    setIsReconnecting,
    setReconnectAttempts,
    setNeedsNewSession,
    setPreChatState,
    setIsSending,
    isMounted,
    processedItemIds,
    sseClient,
    isReconnectingRef,
    initialSessionEstablished,
    sessionToReconnectRef,
    onNewSessionCreatedRef,
    sessionId,
}) => {
    const initializationAbortControllerRef = useRef(null);
    const initializeSession = useCallback(async (chatSessionIdToResume, options = {}, isReconnectAttempt = false) => {
        if (initializationAbortControllerRef.current) {
            console.log('[initializeSession] Aborting previous in-flight initialization.');
            initializationAbortControllerRef.current.abort();
            initializationAbortControllerRef.current = null;
        }
        if (options?.preChatConfig) {
            console.log('[initializeSession] Pre-chat form requested.', options);
            if (isMounted.current) {
                setTimelineItems([]);
                processedItemIds.current.clear();
                setSessionId(null);
                setSessionContext([]);
                setProcessingStatus('idle');
                setIsSending(false);
                setPreChatState({
                    teamName: options.team_name,
                    config: options.preChatConfig,
                    initialContext: options.context,
                });
            }
            return 'pre-chat-initiated';
        }
        if (isMounted.current) {
            setPreChatState(null);
        }
        const controller = new AbortController();
        initializationAbortControllerRef.current = controller;
        const oldSessionId = sessionId;
        if (oldSessionId && oldSessionId !== chatSessionIdToResume) {
            console.log(`[initializeSession] Terminating previous session ${oldSessionId} (fire and forget).`);
            terminateSession(oldSessionId).catch(err => {
                console.warn(`[Fire and Forget] Failed to terminate old session ${oldSessionId}:`, err.message);
            });
        }
        console.log(`[initializeSession] Starting initialization. Session: ${chatSessionIdToResume || 'NEW'}. Is Reconnect: ${isReconnectAttempt}`);
        if (!isAuthenticated) {
            console.warn("[initializeSession] Skipped: Not authenticated.");
            return null;
        }
        if (isMounted.current) {
            setIsInitializing(true);
            setProcessingStatus('processing');
            removeMessage('auth-error');
            setHasAuthFailed(false);
            setIsSubscriptionRequired(false);
        }
        if (sseClient.current) {
            sseClient.current.disconnect();
            sseClient.current = null;
        }
        if (!isReconnectingRef.current) {
            setSessionId(null);
        }
        try {
            const token = getAccessToken();
            if (!token) throw new Error('No valid token found. Please log in again.');
            const sessionResponse = await createOrResumeSession(chatSessionIdToResume, options, controller.signal);
            const newSessionId = sessionResponse.session_id;
            if (!newSessionId) throw new Error('Failed to get a valid session ID from API');
            if (!isMounted.current) return null;
            if (!isReconnectAttempt) {
                setTimelineItems([]);
                processedItemIds.current.clear();
            }
            markSessionAsInitialized();
            if (chatSessionIdToResume && !isReconnectAttempt) {
                console.log(`[initializeSession] Resuming session ${newSessionId}, fetching full history...`);
                let detailedSession = null;
                try {
                    detailedSession = await getChatSessionDetails(newSessionId);
                } catch (detailsError) {
                    if (detailsError.status === 404) {
                        console.warn(`[initializeSession] Attempted to resume session ${newSessionId}, but it was not found (404). Starting it as a new session.`);
                        detailedSession = null;
                    } else {
                        throw detailsError;
                    }
                }
                if (isMounted.current) {
                    setSessionContext(detailedSession?.context || []);
                    setTeamName(detailedSession?.team_name || options.team_name || null);
                }
                if (detailedSession?.conversation_thread && Array.isArray(detailedSession.conversation_thread)) {
                    const historyMessages = detailedSession.conversation_thread;
                    const uniqueMessages = historyMessages.reduce((acc, currentMsg, index, array) => {
                        const isProcessedUserMessage =
                            currentMsg.source === 'User' &&
                            typeof currentMsg.content === 'string' &&
                            (currentMsg.content.startsWith('# Context from') || currentMsg.content.includes('--- Included Resources ---'));
                        if (isProcessedUserMessage) {
                            const prevMsg = array[index - 1];
                            if (prevMsg && prevMsg.source === 'User') {
                                return acc;
                            }
                        }
                        const lastAddedMsg = acc[acc.length - 1];
                        if (lastAddedMsg && lastAddedMsg.source === 'User' && currentMsg.source === 'User' && lastAddedMsg.content === currentMsg.content) {
                            return acc;
                        }
                        acc.push(currentMsg);
                        return acc;
                    }, []);
                    const formattedTimelineItems = uniqueMessages
                        .filter(msg => msg.source !== 'System' && (msg.source === 'User' || msg.source.endsWith('Agent')))
                        .filter(msg => typeof msg.content === 'string')
                        .flatMap((msg, index) => {
                            const baseId = `hist-${newSessionId}-${msg.created_at || index}`;
                            if (msg.source === 'User') {
                                return [{
                                    id: baseId,
                                    type: 'message',
                                    timestamp: new Date(msg.created_at || (new Date(0).getTime() + index * 1000)).toISOString(),
                                    content: msg.content,
                                    source: msg.source,
                                }];
                            }
                            if (msg.source.endsWith('Agent')) {
                                const items = [];
                                const content = msg.content;
                                const thinkingRegex = /\[Thinking\]([\s\S]*?)\[\/Thinking\]/i;
                                const messageRegex = /\[Message\]\s*(\w+):\s*([\s\S]*?)\s*\[\/Message\]/gi;
                                const thinkingMatch = content.match(thinkingRegex);
                                if (thinkingMatch) {
                                    items.push({
                                        id: `${baseId}-thinking`,
                                        type: 'thinking',
                                        timestamp: new Date(msg.created_at || (new Date(0).getTime() + index * 1000)).toISOString(),
                                        content: thinkingMatch[1].trim(),
                                        source: msg.source,
                                    });
                                }
                                const messageMatches = [...content.matchAll(messageRegex)];
                                messageMatches.forEach((messageMatch, matchIndex) => {
                                    const recipient = messageMatch[1];
                                    const messageContent = messageMatch[2].trim();
                                    if (recipient.toLowerCase() === 'user') {
                                        const messageTimestamp = new Date(msg.created_at || (new Date(0).getTime() + index * 1000 + 1 + matchIndex)).toISOString();
                                        items.push({
                                            id: `${baseId}-message-${matchIndex}`,
                                            type: 'message',
                                            timestamp: messageTimestamp,
                                            content: messageContent,
                                            source: msg.source,
                                        });
                                    }
                                });
                                return items;
                            }
                            return [];
                        }).sort((a, b) => parseTimestamp(a.timestamp) - parseTimestamp(b.timestamp));
                    if (isMounted.current) {
                        setTimelineItems(formattedTimelineItems);
                        formattedTimelineItems.forEach(item => processedItemIds.current.add(item.id));
                    }
                } else {
                    if (isMounted.current) {
                        setTimelineItems([]);
                        processedItemIds.current.clear();
                    }
                }
            } else if (!chatSessionIdToResume) {
                if (isMounted.current) {
                    setTimelineItems([]);
                    processedItemIds.current.clear();
                    setSessionContext(options.context || []);
                    if (options.team_name) {
                        setTeamName(options.team_name);
                    }
                }
            } else {
                console.log(`[initializeSession] Reconnecting to session ${newSessionId}. Preserving timeline.`);
            }
            if (isMounted.current) {
                setSessionId(newSessionId);
                setProcessingStatus('success');
                removeMessage('reconnect-notice');
                removeMessage('reconnect-error');
                removeMessage('auth-error');
                if (!initialSessionEstablished.current) {
                    initialSessionEstablished.current = true;
                }
            }
            if (chatSessionIdToResume === null && typeof onNewSessionCreatedRef.current === 'function') {
                onNewSessionCreatedRef.current(newSessionId);
            }
            return newSessionId;
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('[initializeSession] Previous session initialization was aborted as requested.');
                return null;
            }
            console.error(`[initializeSession] Final error during initialization:`, error);
            if (isReconnectingRef.current && error.status === 404 && chatSessionIdToResume) {
                console.warn(`[initializeSession] Reconnect failed: Session ${chatSessionIdToResume} not found (404). Stopping reconnect loop.`);
                if (isMounted.current) {
                    setIsReconnecting(false);
                    setReconnectAttempts(0);
                    sessionToReconnectRef.current = null;
                    
                    // Explicitly remove the transient reconnect notice
                    removeMessage('reconnect-notice');
                    
                    // Add a persistent error message with a different ID so useSessionWithAuth doesn't clear it
                    addMessage({
                        id: 'reconnect-error',
                        text: "Your previous session could not be found. It may have expired. Please start a new session.",
                        severity: 'error',
                        isLoading: false,
                        action: {
                            label: "Start New Chat",
                            onClick: () => {
                                removeMessage('reconnect-error');
                                setNeedsNewSession(true);
                            }
                        }
                    });
                    setTimelineItems([]);
                    setProcessingStatus('idle');
                    setSessionId(null);
                }
                return null;
            }
            if (error.status === 404 && chatSessionIdToResume) {
                console.warn(`[initializeSession] Session ${chatSessionIdToResume} not found (404). Re-throwing for caller to handle.`);
                throw error;
            }
            if (isMounted.current) {
                if (error.status === 429) {
                    console.warn(`[initializeSession] 429 Limit Exceeded.`);
                    addMessage({
                        id: 'usage-limit',
                        text: "You have reached your daily usage limit. Please check back tomorrow or upgrade your plan.",
                        severity: 'warning'
                    });
                    setHasAuthFailed(false);
                    removeMessage('auth-error');
                    setProcessingStatus('error');
                    resetSessionInitialization();
                    setSessionId(null);
                    removeMessage('reconnect-notice');
                    return null;
                }
                const isAuthError = error.message?.toLowerCase().includes('unauthorized') ||
                                      error.message?.toLowerCase().includes('token') ||
                                      error.message?.toLowerCase().includes('authentication failed');
                if (isReconnectingRef.current && !isAuthError) {
                    console.warn(`[initializeSession] Reconnect attempt failed: ${error.message}. Loop will continue.`);
                    return null;
                } else {
                    addMessage({ id: 'auth-error', text: error.message || 'Failed to initialize session', severity: 'error' });
                    setHasAuthFailed(true);
                    setProcessingStatus('error');
                    resetSessionInitialization();
                    setSessionId(null);
                    removeMessage('reconnect-notice');
                    return null;
                }
            }
            return null;
        } finally {
            if (isMounted.current) {
                setIsInitializing(false);
            }
            if (initializationAbortControllerRef.current === controller) {
                initializationAbortControllerRef.current = null;
            }
        }
    }, [
        isAuthenticated, isMounted, sseClient, isReconnectingRef, markSessionAsInitialized,
        addMessage, removeMessage, resetSessionInitialization, setIsSubscriptionRequired,
        setIsInitializing, setProcessingStatus, setTimelineItems, setHasAuthFailed,
        setSessionId, setSessionContext, setTeamName, setIsReconnecting, setReconnectAttempts, setNeedsNewSession,
        processedItemIds, initialSessionEstablished, sessionToReconnectRef, onNewSessionCreatedRef,
        sessionId, setPreChatState, setIsSending
    ]);
    return { initializeSession };
};